/**
 * Nome do arquivo que o SERVIDOR mandou salvar (E65.F6.C04 — F-E65.F6-04).
 *
 * O backend sanea o nome (RFC 6266) e corrige a extensão pelo mime que validou
 * na ingestão: um upload chamado `evidencia.exe` com bytes PNG desce como
 * `evidencia.png`. Se o cliente ignorar o header e usar o `original_filename`
 * da listagem, o navegador grava de volta o `.exe` e a garantia do servidor
 * morre no último metro.
 *
 * O parser lê `filename*` antes de `filename`: é ele que carrega o nome com
 * acento, percent-encoded em UTF-8. Sem header legível, devolve o fallback —
 * baixar com um nome razoável é melhor do que não baixar.
 */

export interface ArquivoBaixado {
  blob: Blob;
  /** Nome a usar no `download` do link — o do servidor, quando ele o envia. */
  filename: string;
}

const _ESTENDIDO = /filename\*\s*=\s*UTF-8''([^;]+)/i;
const _SIMPLES = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i;

export function nomeDoAnexo(contentDisposition: unknown, fallback: string): string {
  if (typeof contentDisposition !== 'string') return fallback;

  const estendido = _ESTENDIDO.exec(contentDisposition);
  if (estendido?.[1]) {
    try {
      const decodificado = decodeURIComponent(estendido[1].trim());
      if (decodificado) return decodificado;
    } catch {
      // Percent-encoding quebrado: cai no `filename` simples abaixo em vez de
      // derrubar o download inteiro por causa do nome.
    }
  }

  const simples = _SIMPLES.exec(contentDisposition);
  const bruto = (simples?.[1] ?? simples?.[2] ?? '').trim();
  return bruto || fallback;
}

/**
 * Entrega ao navegador o arquivo já baixado, com o nome que o servidor mandou.
 *
 * O link temporário é o único caminho que preserva o NOME: `window.open` sobre
 * uma `blob:` URL salva com o identificador aleatório da URL. O `revokeObjectURL`
 * fecha o vazamento de memória que sobra quando a página vive (a lista de casos
 * fica aberta e a pessoa baixa vários documentos seguidos).
 */
export function salvarArquivoBaixado(arquivo: ArquivoBaixado): void {
  const url = URL.createObjectURL(arquivo.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = arquivo.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
